import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Clock, Users, DollarSign, Shield, Building2, AlertTriangle,
  Smartphone, Tablet, ArrowLeft, ArrowRight, AlertCircle, Lightbulb,
} from 'lucide-react';
import Hero from '../../components/marketing/Hero';
import SectionWrapper from '../../components/marketing/SectionWrapper';
import FeatureCard from '../../components/marketing/FeatureCard';
import ScreenshotShowcase from '../../components/marketing/ScreenshotShowcase';
import TestimonialCarousel from '../../components/marketing/TestimonialCarousel';
import CTABanner from '../../components/marketing/CTABanner';
import { ALL_PHOTOS } from '../../components/marketing/photoPool';
import { RTL_LANGUAGES } from '../../i18n';

export default function HomePage() {
  const { t, i18n } = useTranslation('marketing');
  const tc = useTranslation('common').t;
  const isRTL = RTL_LANGUAGES.includes(i18n.language);
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const overviewFeatures = [
    { icon: Clock, title: t('overview.attendance_title'), description: t('overview.attendance_desc'), color: '#0891b2' },
    { icon: Users, title: t('overview.parents_title'), description: t('overview.parents_desc'), color: '#16a34a' },
    { icon: DollarSign, title: t('overview.budget_title'), description: t('overview.budget_desc'), color: '#ea580c' },
    { icon: AlertTriangle, title: t('overview.emergency_title'), description: t('overview.emergency_desc'), color: '#dc2626' },
    { icon: Building2, title: t('overview.network_title'), description: t('overview.network_desc'), color: '#7c3aed' },
    { icon: Shield, title: t('overview.security_title'), description: t('overview.security_desc'), color: '#0d9488' },
  ];

  const parentFeatures = [
    { text: t('parents.feature1_title') + ' \u2014 ' + t('parents.feature1_desc') },
    { text: t('parents.feature2_title') + ' \u2014 ' + t('parents.feature2_desc') },
    { text: t('parents.feature3_title') + ' \u2014 ' + t('parents.feature3_desc') },
    { text: t('parents.feature4_title') + ' \u2014 ' + t('parents.feature4_desc') },
    { text: t('parents.feature5_title') + ' \u2014 ' + t('parents.feature5_desc') },
  ];

  const kindergartenFeatures = [
    { text: t('kindergartens.feature1_title') + ' \u2014 ' + t('kindergartens.feature1_desc') },
    { text: t('kindergartens.feature2_title') + ' \u2014 ' + t('kindergartens.feature2_desc') },
    { text: t('kindergartens.feature3_title') + ' \u2014 ' + t('kindergartens.feature3_desc') },
    { text: t('kindergartens.feature4_title') + ' \u2014 ' + t('kindergartens.feature4_desc') },
  ];

  const testimonials = [
    { text: t('testimonials.t1_text'), name: t('testimonials.t1_name'), role: t('testimonials.t1_role') },
    { text: t('testimonials.t2_text'), name: t('testimonials.t2_name'), role: t('testimonials.t2_role') },
    { text: t('testimonials.t3_text'), name: t('testimonials.t3_name'), role: t('testimonials.t3_role') },
  ];

  return (
    <>
      <Hero />

      {/* Challenge & Solution */}
      <section className="py-16 md:py-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{t('challenge_solution.title')}</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-red-50 rounded-2xl p-8 border-e-4 border-red-400"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-red-700">{t('challenge_solution.challenge_title')}</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">{t('challenge_solution.challenge_desc')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-cyan-50 rounded-2xl p-8 border-e-4 border-cyan-400"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-cyan-600" />
                </div>
                <h3 className="text-2xl font-bold text-cyan-700">{t('challenge_solution.solution_title')}</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">{t('challenge_solution.solution_desc')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <SectionWrapper
        id="overview"
        title={t('overview.title')}
        subtitle={t('overview.subtitle')}
        background="white"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {overviewFeatures.map((f, i) => (
            <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} color={f.color} index={i} />
          ))}
        </div>
      </SectionWrapper>

      {/* Parents preview */}
      <SectionWrapper
        title={t('parents.section_title')}
        subtitle={t('parents.section_subtitle')}
        background="gray"
      >
        <ScreenshotShowcase
          imageSrcs={ALL_PHOTOS}
          alt="Parent mobile interface"
          direction="left"
          title={t('parents.title')}
          subtitle={t('parents.subtitle')}
          features={parentFeatures}
          accentColor="#16a34a"
          placeholderIcon={Smartphone}
        />
        <div className="text-center mt-10">
          <Link
            to="/parents"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm text-green-600 font-semibold hover:bg-green-50 rounded-xl transition-colors"
          >
            {tc('nav.parents')}
            <ArrowIcon className="w-4 h-4" />
          </Link>
        </div>
      </SectionWrapper>

      {/* Kindergartens preview */}
      <SectionWrapper
        title={t('kindergartens.section_title')}
        subtitle={t('kindergartens.section_subtitle')}
        background="white"
      >
        <ScreenshotShowcase
          imageSrcs={ALL_PHOTOS}
          alt="Kindergarten tablet interface"
          direction="right"
          title={t('kindergartens.title')}
          subtitle={t('kindergartens.subtitle')}
          features={kindergartenFeatures}
          accentColor="#0891b2"
          placeholderIcon={Tablet}
        />
        <div className="text-center mt-10">
          <Link
            to="/kindergartens"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm text-cyan-600 font-semibold hover:bg-cyan-50 rounded-xl transition-colors"
          >
            {tc('nav.kindergartens')}
            <ArrowIcon className="w-4 h-4" />
          </Link>
        </div>
      </SectionWrapper>

      {/* Networks preview */}
      <SectionWrapper
        title={t('networks.section_title')}
        subtitle={t('networks.section_subtitle')}
        background="gray"
      >
        <ScreenshotShowcase
          imageSrcs={ALL_PHOTOS}
          alt="Network management dashboard"
          direction="left"
          title={t('networks.title')}
          subtitle={t('networks.subtitle')}
          features={[
            { text: t('networks.feature1_title') + ' \u2014 ' + t('networks.feature1_desc') },
            { text: t('networks.feature2_title') + ' \u2014 ' + t('networks.feature2_desc') },
            { text: t('networks.feature3_title') + ' \u2014 ' + t('networks.feature3_desc') },
            { text: t('networks.feature4_title') + ' \u2014 ' + t('networks.feature4_desc') },
            { text: t('networks.feature5_title') + ' \u2014 ' + t('networks.feature5_desc') },
          ]}
          accentColor="#7c3aed"
          placeholderIcon={Building2}
        />
        <div className="text-center mt-10">
          <Link
            to="/networks"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm text-purple-600 font-semibold hover:bg-purple-50 rounded-xl transition-colors"
          >
            {tc('nav.networks')}
            <ArrowIcon className="w-4 h-4" />
          </Link>
        </div>
      </SectionWrapper>

      {/* Emergency preview */}
      <SectionWrapper
        title={t('emergency.section_title')}
        subtitle={t('emergency.section_subtitle')}
        background="gradient"
      >
        <ScreenshotShowcase
          imageSrcs={ALL_PHOTOS}
          alt="Emergency call center dashboard"
          direction="right"
          title={t('emergency.title')}
          subtitle={t('emergency.subtitle')}
          features={[
            { text: t('emergency.feature1_title') + ' \u2014 ' + t('emergency.feature1_desc') },
            { text: t('emergency.feature2_title') + ' \u2014 ' + t('emergency.feature2_desc') },
            { text: t('emergency.feature3_title') + ' \u2014 ' + t('emergency.feature3_desc') },
            { text: t('emergency.feature4_title') + ' \u2014 ' + t('emergency.feature4_desc') },
          ]}
          accentColor="#dc2626"
          placeholderIcon={AlertTriangle}
        />
        <div className="text-center mt-10">
          <Link
            to="/emergency"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm text-red-600 font-semibold hover:bg-red-50 rounded-xl transition-colors"
          >
            {tc('nav.emergency')}
            <ArrowIcon className="w-4 h-4" />
          </Link>
        </div>
      </SectionWrapper>

      {/* Testimonials */}
      <SectionWrapper
        title={t('testimonials.title')}
        subtitle={t('testimonials.subtitle')}
        background="white"
      >
        <TestimonialCarousel testimonials={testimonials} />
      </SectionWrapper>

      <CTABanner
        title={t('cta.title')}
        subtitle={t('cta.subtitle')}
        buttonText={t('cta.button')}
      />
    </>
  );
}
